import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router';
import InfiniteScroll from 'react-infinite-scroll-component';
import tinycolor from "tinycolor2";

// API/Components
import axiosInstance from '../components/axios';
import obvmConfig from '../config';
import { dictToQueryString, formatDateTime } from '../components/enums';
import DateRangeFilterControl from '../components/filters/date_range_filter_control';
import EventTypeFilterDialog from '../components/filters/event_type_filter_dialog';

import {
	AlarmEventToStatusText,
	EventTypeToColor,
} from '../components/alarm_enums';

// Material UI
import {
	Box,
	IconButton,
	Paper,
	Toolbar,
	Typography,
	Table,
	TableContainer,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Tooltip,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ColorLensIcon from '@mui/icons-material/ColorLens';
	
const eventsPerPage = obvmConfig.EVENTS.EVENTS_PER_PAGE;

// System Trend sub-page rendering (Location Name, Title)
export default function SystemEvents() {
	const { locationNum } = useParams();

	// State Management
	const [locationInfo, setLocationInfo] = useState({
		locationName: '',
		numStrings: 0,
		numUnits: 0,
		numTemps: 0,
	});
	
	const [events, setEvents] = useState([]);
	const [moreData, setMoreData] = useState(true);
	const [apiPage, setApiPage] = useState(1); // Page number for the API call
	
	// Save filtering states and colorization
	const [isColorized, setIsColorized] = useState(true);
	const [dateRangeFiltering, setDateRangeFiltering] = useState({
		start: '',
		end: '',
	});
	const [eventTypeFiltering, setEventTypeFiltering] = useState({
		acknowledged: true,
		cleared: true,
		type: [],
	});

	// Dialog state management
	const [dialogOpen, setDialogOpen] = useState(false);
	
	// Get information about the location id specified (name, size, num of temps)
	useEffect(() => {
		axiosInstance.get(`/loc/${locationNum}/size/`).then(response => {
			const locationInfo = response.data;
			setLocationInfo({
				locationName: locationInfo.location_name,
				serialNumber: locationInfo.serial_number,
			});
		});
	}, [locationNum]);
	
	useEffect(() => {
		getEventData(true);
	}, [locationNum, dateRangeFiltering, eventTypeFiltering]);

	// Get event data from the API
	function getEventData(overwrite = true) {

		// Only attempt to get new data if there is more data to get
		if (moreData) {
			// Set the events api endpoint URL
			let url = `/loc/${locationNum}/events`;

			// Filter events based on name, hierarchy, and type options
			let queryParams = {};

			// Add the exclude acknowledged filter to the query parameters
			if (eventTypeFiltering && !eventTypeFiltering.acknowledged) {
				queryParams['ack'] = 'false';
			}

			// Add the exclude cleared filter to the query parameters
			if (eventTypeFiltering && !eventTypeFiltering.cleared) {
				queryParams['clear'] = 'false';
			}
			
			if (Array.isArray(eventTypeFiltering.type) && eventTypeFiltering.type.length > 0) {
				queryParams['type'] = eventTypeFiltering.type.join(',');
			}

			// Add the date range filter to the query parameters
			if (dateRangeFiltering.start) {
				queryParams['start'] = dateRangeFiltering.start;
			}

			if (dateRangeFiltering.end) {
				queryParams['end'] = dateRangeFiltering.end;
			}

			// Add the page number to the query parameters
			if (apiPage > 1) {
				queryParams['page'] = apiPage;
			}

			// Convert the query parameters to a query string
			url +=  dictToQueryString(queryParams);

			axiosInstance.get(url).then(response => {
				if (overwrite) {
					setEvents(response.data);
				} else {
					setEvents(prevEvents => [...prevEvents, ...response.data]);
				}

				if (response.data.length >= eventsPerPage) {
					setApiPage(apiPage + 1);
					setMoreData(true);
				} else {
					setMoreData(false);
				}
			});
		}
	}

	// Update the actual filtering options when the dialog is closed
	const handleDialogClose = (dialogFiltering) => {
		setDialogOpen(false);

		setApiPage(1);
		setMoreData(true);

		setEventTypeFiltering(dialogFiltering);
	};

	// Update the date range filtering when the date range filter control changes
	const handleDateRangeChange = useCallback((start_date, end_date) => {
		setApiPage(1);
		setMoreData(true);

		setDateRangeFiltering((prevDateRangeFiltering) => {
			if (prevDateRangeFiltering.start === start_date && prevDateRangeFiltering.end === end_date) {
				return prevDateRangeFiltering;
			}

			return {
				start: start_date,
				end: end_date,
			};
		});
	}, []);

	const DateTimeStyle = {
		minWidth: '200px',
		width: '200px',
	};

	const ClearedStyle = {
		minWidth: '200px',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	const EventStyle = {
		minWidth: '400px',
		width: '30%',
	};

	const NoteStyle = {
		minWidth: '200px',
		width: '20%',
	};

	const AckStyle = {
		minWidth: '200px',
		width: '200px',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	const AckUserStyle = {
		minWidth: '160px',
		width: '10%',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	const RetrievedStyle = {
		minWidth: '200px',
		width: '200px',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	return (
		<Paper
			sx={{
				display: 'flex',
				flexDirection: 'column',
				boxShadow: '0',
				overflow: 'hidden',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				{ locationInfo.locationName } (SN {locationInfo.serialNumber})
			</Typography>
			<Toolbar disableGutters sx={{ paddingTop: 2, paddingX: 1.5, marginBottom: 1 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center" width='100%'>
					<Typography
						align="left"
						fontFamily='Saira,sans-serif'
						fontWeight='600'
						sx={{ flexGrow: 1}}
					>
						Events Summary
					</Typography>
					<Tooltip title="Toggle type colorization">
						<IconButton
							sx={{
								marginX: 1,
								border: '1px solid',
								borderRadius: 1,
								color: isColorized ? 'primary.main' : 'grey.500',
							}}
							onClick={() => setIsColorized(!isColorized)}
						>
							<ColorLensIcon />
						</IconButton>
					</Tooltip>
					<IconButton
						sx={{
							border: '1px solid',
							borderRadius: 1,
							borderColor: dialogOpen ? 'primary.main' : 'grey.500',
							marginRight: 1,
						}}
						onClick={() => setDialogOpen(true)}
					>
						<FilterListIcon />
						<Typography
							fontFamily='Saira,sans-serif'
							fontWeight='600'
							sx={{ marginLeft: 1 }}
						>
							Type
						</Typography>
					</IconButton>
					<DateRangeFilterControl onFilterChange={handleDateRangeChange} />
				</Box>
			</Toolbar>
			<EventTypeFilterDialog dialogOpen={dialogOpen} handleDialogClose={handleDialogClose} />
			<div
				id="scrollableDiv"
				style={{
					height: 'calc(100vh - 225px)',
					overflow: 'auto',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<InfiniteScroll
					dataLength={events.length}
					next={() => { getEventData(false) }}
					hasMore={moreData}
					scrollableTarget="scrollableDiv"
					style={{ overflow: 'visible' }}
					loader={
						<Typography fontFamily='Saira,sans-serif' fontWeight='600' sx={{ textAlign: 'center'}}>
							Loading events...
						</Typography>
					}
					endMessage={
						<Typography fontFamily='Saira,sans-serif' fontWeight='600' sx={{ textAlign: 'center'}}>
							No more events to load.
						</Typography>
					}
				>
					<TableContainer sx={{ margin: 0, padding: 0 }}>
						<Table size="small" sx={{ margin: 0, borderSpacing: 0 }}>
							<TableHead>
								<TableRow>
									<TableCell sx={{ ...DateTimeStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Date/Time
									</TableCell>
									<TableCell sx={{ ...ClearedStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Date/Time Cleared
									</TableCell>
									<TableCell sx={{ ...EventStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Event
									</TableCell>
									<TableCell sx={{ ...NoteStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Note
									</TableCell>
									<TableCell sx={{ ...AckStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Acknowledged
									</TableCell>
									<TableCell sx={{ ...AckUserStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Ack. User
									</TableCell>
									<TableCell sx={{ ...RetrievedStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Retrieved
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{events.map((event, index) => (
									<TableRow
										key={index}
										sx={{
											backgroundColor: isColorized ? (
												tinycolor(EventTypeToColor(event.alarm_type, event.number)).lighten(15).toString()
											) : ( 
												index % 2 === 0 ? '#FFFFFF' : '#F2F2F2'
											),
											'&:hover': {
												backgroundColor: isColorized ? EventTypeToColor(event.alarm_type, event.number) : '#D9D9D9',
											},
										}}
									>
										<TableCell sx={DateTimeStyle} size="small" padding="checkbox">
											{formatDateTime(event.datetime)}
										</TableCell>
										<TableCell sx={ClearedStyle} size="small" padding="checkbox">
											{formatDateTime(event.clear_datetime)}
										</TableCell>
										<TableCell sx={EventStyle} size="small" padding="checkbox">
											{AlarmEventToStatusText(event)}
										</TableCell>
										<TableCell sx={NoteStyle} size="small" padding="checkbox">
											{event.note_trimmed}
										</TableCell>
										<TableCell sx={AckStyle} size="small" padding="checkbox">
											{formatDateTime(event.ack_datetime)}
										</TableCell>
										<TableCell sx={AckUserStyle} size="small" padding="checkbox">
											{event.ack_user_trimmed}
										</TableCell>
										<TableCell sx={RetrievedStyle} size="small" padding="checkbox">
											{formatDateTime(event.retrieve_datetime)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</InfiniteScroll>
			</div>
		</Paper>
	);


}